// Keyboard state with per-frame edge detection.
//
// Touch is ADDITIVE: on-screen controls (mounted only on touch devices, see
// main.ts) push the SAME virtual keys the keyboard feeds, so input.left() /
// right() / jumpPressed() / echoPressed() / etc. all reflect touch without any
// branching downstream. Desktop keyboard behaviour is unchanged.

const held = new Set<string>();
const pressedThisFrame = new Set<string>();

// Virtual keys currently held down by a touch control. Kept separate from the
// keyboard `held` set so a finger lifting can't clear a physically-held key
// (and vice versa). Reads below union the two.
const touchHeld = new Set<string>();

const GAME_KEYS = new Set([
  'arrowleft', 'arrowright', 'arrowup', 'arrowdown',
  'a', 'd', 'w', 's', ' ', 'r', 'x', 'enter', 'escape', 'u', 'v', 'i',
  '1', '2', '3',
]);

/** True on touch-capable devices (iPad / iPhone / Android). Used by main.ts to
 *  decide whether to mount the on-screen controls and default to the 2D mode. */
export function isTouchDevice(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    (navigator.maxTouchPoints > 0 ||
      ('ontouchstart' in window))
  );
}

export function initInput(): void {
  window.addEventListener('keydown', (e) => {
    const k = normalize(e.key);
    if (GAME_KEYS.has(k)) e.preventDefault();
    if (!held.has(k)) pressedThisFrame.add(k);
    held.add(k);
  });
  window.addEventListener('keyup', (e) => {
    held.delete(normalize(e.key));
  });
  window.addEventListener('blur', () => {
    held.clear();
    touchHeld.clear();
  });
}

// ---- touch → virtual-key bridge ------------------------------------------------

/** Latch a virtual key as held by touch, registering a press edge on the down. */
export function touchKeyDown(key: string): void {
  const k = key.toLowerCase();
  if (!touchHeld.has(k) && !held.has(k)) pressedThisFrame.add(k);
  touchHeld.add(k);
}

/** Release a touch-held virtual key. */
export function touchKeyUp(key: string): void {
  touchHeld.delete(key.toLowerCase());
}

/** Fire a one-shot press edge from a touch (for tap buttons like ECHO / WIPE /
 *  ENTER) without leaving the key held. */
export function touchTap(key: string): void {
  pressedThisFrame.add(key.toLowerCase());
}

/** Drop every touch-held key (e.g. when controls unmount or state changes). */
export function clearTouchHeld(): void {
  touchHeld.clear();
}

function normalize(key: string): string {
  return key.length === 1 ? key.toLowerCase() : key.toLowerCase();
}

const isHeld = (k: string): boolean => held.has(k) || touchHeld.has(k);
const wasPressed = (k: string): boolean => pressedThisFrame.has(k);

export const input = {
  left: () => isHeld('arrowleft') || isHeld('a'),
  right: () => isHeld('arrowright') || isHeld('d'),
  jumpHeld: () => isHeld(' ') || isHeld('arrowup') || isHeld('w'),
  jumpPressed: () => wasPressed(' ') || wasPressed('arrowup') || wasPressed('w'),
  echoPressed: () => wasPressed('r'),
  resetPressed: () => wasPressed('x'),
  confirmPressed: () => wasPressed('enter') || wasPressed(' '),
  mode3dPressed: () => wasPressed('3'),
  // menu mode-select navigation
  menuUpPressed: () => wasPressed('arrowup') || wasPressed('w'),
  menuDownPressed: () => wasPressed('arrowdown') || wasPressed('s'),
  digitPressed: (d: string) => wasPressed(d),
  // unlock-overlay controls
  unlockPressed: () => wasPressed('u'),
  adPressed: () => wasPressed('v'),
  backPressed: () => wasPressed('escape'),
  // intro cinematic
  replayIntroPressed: () => wasPressed('i'),
  anyPressed: () => pressedThisFrame.size > 0,
  /** Call once per render frame, AFTER all reads. */
  endFrame: () => pressedThisFrame.clear(),
};
